import React, { useState, useCallback, forwardRef } from 'react';
import { Star } from '@phosphor-icons/react';
import { cn } from 'src/utils/className';

/**
 * Claude auto-generated component
 */
export type RatingProps = {
  /** Current rating value (1-5) */
  value?: number | null;
  /** Default value for uncontrolled component */
  defaultValue?: number | null;
  /** Maximum rating value */
  max?: number;
  /** Minimum increment for rating values */
  precision?: number;
  /** Size of the rating component */
  size?: 'small' | 'medium' | 'large';
  /** Whether the rating is read-only */
  readOnly?: boolean;
  /** Whether the rating is disabled */
  disabled?: boolean;
  /** Callback fired when the rating value changes */
  onChange?: (event: React.SyntheticEvent, newValue: number | null) => void;
  /** Callback fired when the hover state changes */
  onChangeActive?: (event: React.SyntheticEvent, newHover: number) => void;
  /** Function to get the label text for a given value */
  getLabelText?: (value: number) => string;
  /** Custom class name */
  className?: string;
  /** Name attribute for form submission */
  name?: string;
  /** Show hover feedback */
  showHoverFeedback?: boolean;
  /** Custom icon component to use instead of the default star */
  icon?: React.ComponentType<{
    className?: string;
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  }>;
  /** Custom empty icon component to use instead of the default empty star */
  emptyIcon?: React.ComponentType<{
    className?: string;
    weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
  }>;
};

const sizeClasses = {
  small: 'w-4 h-4',
  medium: 'w-5 h-5',
  large: 'w-6 h-6',
};

const defaultGetLabelText = (value: number) =>
  `${value} Star${value !== 1 ? 's' : ''}`;

export const Rating = forwardRef<HTMLDivElement, RatingProps>(
  (
    {
      value: valueProp,
      defaultValue = null,
      max = 5,
      precision = 1,
      size = 'medium',
      readOnly = false,
      disabled = false,
      onChange,
      onChangeActive,
      getLabelText = defaultGetLabelText,
      className,
      name,
      showHoverFeedback = false,
      icon: IconComponent,
      emptyIcon: EmptyIconComponent,
      ...rest
    },
    ref,
  ) => {
    const [valueState, setValueState] = useState(defaultValue);
    const [hover, setHover] = useState(-1);

    const value = valueProp !== undefined ? valueProp : valueState;
    const isControlled = valueProp !== undefined;

    const handleClick = useCallback(
      (event: React.MouseEvent, newValue: number) => {
        if (readOnly || disabled) return;

        if (!isControlled) {
          setValueState(newValue);
        }

        if (onChange) {
          onChange(event, newValue);
        }
      },
      [readOnly, disabled, isControlled, onChange],
    );

    const handleMouseEnter = useCallback(
      (event: React.MouseEvent, newHover: number) => {
        if (readOnly || disabled) return;

        setHover(newHover);
        if (onChangeActive) {
          onChangeActive(event, newHover);
        }
      },
      [readOnly, disabled, onChangeActive],
    );

    const handleMouseLeave = useCallback(
      (event: React.MouseEvent) => {
        if (readOnly || disabled) return;

        setHover(-1);
        if (onChangeActive) {
          onChangeActive(event, -1);
        }
      },
      [readOnly, disabled, onChangeActive],
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent, starValue: number) => {
        if (readOnly || disabled) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick(event as any, starValue);
        }
      },
      [readOnly, disabled, handleClick],
    );

    // Use custom icons or default to Phosphor Star
    const FilledIcon = IconComponent || Star;
    const EmptyIcon = EmptyIconComponent || Star;

    const stars = [];
    for (let i = 1; i <= max; i++) {
      const isFilled = (hover !== -1 ? hover : value || 0) >= i;
      const isHovered = hover >= i;

      const CurrentIcon = isFilled ? FilledIcon : EmptyIcon;
      const iconWeight = isFilled ? 'fill' : 'regular';

      stars.push(
        <button
          key={i}
          type="button"
          className={cn(
            'p-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded transition-all duration-150',
            !readOnly && !disabled && 'cursor-pointer hover:scale-110',
            readOnly && 'cursor-default',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          onClick={(event) => handleClick(event, i)}
          onMouseEnter={(event) => handleMouseEnter(event, i)}
          onKeyDown={(event) => handleKeyDown(event, i)}
          disabled={disabled}
          tabIndex={readOnly ? -1 : 0}
          aria-label={getLabelText(i)}
          {...(name && i === 1 && { name })}
        >
          <CurrentIcon
            weight={iconWeight}
            className={cn(
              sizeClasses[size],
              'transition-colors duration-150',
              isFilled
                ? isHovered && !readOnly && !disabled
                  ? 'text-primary-400'
                  : 'text-primary'
                : 'text-neutral-300',
              disabled && 'opacity-50',
            )}
          />
        </button>,
      );
    }

    return (
      <div
        ref={ref}
        className={cn('inline-flex items-center gap-1', className)}
        onMouseLeave={handleMouseLeave}
        role={readOnly ? 'img' : 'radiogroup'}
        aria-label={readOnly && value ? getLabelText(value) : undefined}
        {...rest}
      >
        {stars}
        {showHoverFeedback && hover !== -1 && (
          <span className="ml-2 text-sm text-neutral-600">
            {getLabelText(hover)}
          </span>
        )}
      </div>
    );
  },
);

Rating.displayName = 'Rating';
